import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { connect } from "react-redux";
import React, { Component } from "react";
import { style, forceRenderStyles, classes } from "typestyle";
import MapPolygonEditControl from "../../leafletControls/MapPolygonEditControl";
import MapZoomControl from "../../leafletControls/Zoom";
import MapBaseLayersControl, {
  MAPBOX_SATELLITE,
  OSM_RASTER_LAYER,
} from "../../leafletControls/MapBaseLayersControl";
import MapControlConfirm from "./MapControlConfirm";
import config from "../../../config";
import { S3TileAccessLayer } from "./S3";
import LegendControl from "../../leafletControls/LegendControl";
import { Legend } from "../../leafletControls/LegendControl";
import MapControlsLayout from "../../components/MapControlsLayout";
import MapContainerWithUiLayers from "../../components/MapContainerWithUiLayers";
import UploadButtonsControl from "../../leafletControls/UploadButtonsControl";
import RequestGcpSuggestionPopup from "./RequestGcpSuggestionPopup";
import FadeOutContainer from "./FadeOutContainer";
import { RequestGcpSuggestion } from "./types";
import { FlightMarker } from "../matrixCalculator/types";

import { AppDispatch } from "../../../store";
import { getSiteLevelToken } from "../../../redux/accessTokenManagerActions";
import { Err } from "../../../typings";
import { fetchControlPlan } from "../../../apiFacades/bapi/controlPlan";
import {
  fetchMaidenScan,
  fetchPrescan,
} from "../../../apiFacades/bapi/prescan";
import { fetchDesignGradeBoundary } from "../../../apiFacades/bapi/designGradeBoundary";
import { fetchFlightMarkeres } from "../../../apiFacades/bapi/flightMarkers";
import { ISRS, SiteBoundary } from "../../../apiFacades/csu/site";
import { convertGlobalToWgs84 } from "../matrixCalculator/math";

interface SiteAreaEditorProps {
  customerId: string;
  siteId: string;
  value: SiteBoundary;
  srs: ISRS | null;
  onChange: (newBoundary: SiteBoundary) => void;
  onUpdateRequestGcpSuggestion: (requestIsNeeded: boolean) => void;
  requestGcpSuggestion: RequestGcpSuggestion;
  dispatch: AppDispatch;
}

interface SiteAreaEditorState {
  map: L.Map | null;
  legends: Record<string, Legend>;
  value: SiteBoundary | null; // state contains modified site area
}

type FlightMarkersError = {
  payload: {
    body: {
      error: Err;
    };
  };
};

const sourceAreaStyle = {
  weight: 4,
  dashArray: [4, 8],
  color: "#F89900",
};

class SiteAreaEditor extends Component<
  SiteAreaEditorProps,
  SiteAreaEditorState
> {
  mapContainer = React.createRef<HTMLDivElement>();
  sourceAreaPolygon: L.TileLayer;
  state: SiteAreaEditorState = {
    map: null,
    value: null,
    legends: {
      sourceArea: {
        title: "Current Area",
        layerStyle: sourceAreaStyle,
        order: 3,
      },
      newArea: {
        title: "New Area",
        layerStyle: {},
        order: 4,
      },
    },
  };

  handlePolygonChange = (value: SiteBoundary): void => {
    this.setState({
      value,
    });
  };

  componentDidMount(): void {
    // Typestyle uses requestAnimationFrame before put styles into head
    // so style() call is asynchronous. We need to ensure that all map styles are rendered
    // by calling forceRenderStyles
    forceRenderStyles();

    this.setState(
      {
        map: L.map(this.mapContainer.current as HTMLDivElement, {
          zoomControl: false,
          attributionControl: false,
        }).setView([50, 50], 3),
      },
      () => {
        setTimeout(() => {
          this.addControlPlanLayer();
          this.addOrthophotoLayer();
          this.addDgBoundaryLayer();
          this.addFlightMarkersLayer();
        }, 1);
        this.mapRenderSourcePolygon();
      },
    );
  }

  async addControlPlanLayer(): Promise<void> {
    const { customerId, siteId, dispatch } = this.props;
    const accessToken = await dispatch(getSiteLevelToken(customerId, siteId));
    dispatch(fetchControlPlan(customerId, siteId)).then(
      (controlPlan) => {
        // @ts-ignore
        const layer = new S3TileAccessLayer(controlPlan.tilesPath, {
          accessToken,
          tms: true,
          maxZoom: controlPlan.maxZoomLevel,
          zIndex: 2,
        });
        this.state.map && this.state.map.addLayer(layer);
      },
      (error: Err) => {
        const { message, code } = error;
        if (code === "CONTROL_PLAN_NOT_FOUND") {
          console.warn("control plan not found");
        } else {
          console.error(
            `unknown error during requesting control plan: ${code} ${message}`,
          );
        }
      },
    );
  }

  addDgBoundaryLayer(): void {
    const { customerId, siteId, dispatch } = this.props;
    dispatch(fetchDesignGradeBoundary(customerId, siteId)).then(
      (dgJson) => {
        const dgBoundaryStyle = {
          color: "#cc0000",
        };
        const layer = L.geoJSON(dgJson, {
          style: {
            color: "#f00",
          },
        });
        this.state.map && this.state.map.addLayer(layer);
        this.setState({
          legends: {
            ...this.state.legends,
            dg: {
              title: "Design Grade Boundary",
              layerStyle: dgBoundaryStyle,
              order: 5,
            },
          },
        });
      },
      (error: Err) => {
        const { message, code } = error;
        if (code === "DESIGN_GRADE_BOUNDARY_NOT_FOUND") {
          console.warn("design grade boundary not found");
        } else {
          console.error(
            `unknown error during requesting design grade boundary: ${code} ${message}`,
          );
        }
      },
    );
  }

  addFlightMarkersLayer(): void {
    const { customerId, siteId, srs, dispatch } = this.props;

    const epsg = srs?.assumedSrs?.intermediateSrsProj4 || srs?.globalSrs?.proj4;
    if (epsg == null) {
      const message =
        "EPSG string is not set for this site, cannot show markers";
      console.warn(message);
      return;
    }

    dispatch(fetchFlightMarkeres(customerId, siteId)).then(
      (flightMarkers) => {
        const activeFlightMarkers: FlightMarker[] = flightMarkers.filter(
          ({ tsArchived }: FlightMarker) => !tsArchived,
        );
        const groupLayer = activeFlightMarkers.reduce(
          (groupLayer, flightMarker) => {
            const wgs84Coords = convertGlobalToWgs84(
              epsg,
              flightMarker.operatorCoords,
            );
            const coords = {
              lat: wgs84Coords.y,
              lng: wgs84Coords.x,
            };
            const flightMarkerLayer = L.marker(coords, {
              icon: L.icon({
                iconSize: [24, 24],
                iconUrl: `icon-${flightMarker.isQC ? "qc" : "gcp"}.svg`,
              }),
            });
            groupLayer.addLayer(flightMarkerLayer);
            return groupLayer;
          },
          L.layerGroup(),
        );
        this.state.map && this.state.map.addLayer(groupLayer);
        this.setState({
          legends: {
            ...this.state.legends,
            qc: {
              title: "Quality Check point",
              iconUrl: "icon-qc.svg",
              order: 2,
            },
            gcp: {
              title: "Ground Control Point",
              iconUrl: "icon-gcp.svg",
              order: 1,
            },
          },
        });
      },
      ({ payload }: FlightMarkersError) => {
        const { message, code } = payload && payload.body.error;
        if (code === "FLIGHT_MARKERS_NOT_FOUND") {
          console.warn("flight markers not found");
        } else {
          console.error(
            `unknown error during requesting flight markers layer: ${code} ${message}`,
          );
        }
      },
    );
  }

  private async addOrthophotoLayer() {
    const orthophotoTilesPath = await this.fetchOrthophotoTiles();

    if (!orthophotoTilesPath) {
      // nothing to render
      return;
    }

    const { dispatch, customerId, siteId } = this.props;
    const accessToken = await dispatch(getSiteLevelToken(customerId, siteId));

    // @ts-ignore broken types for this class :(
    const layer = new S3TileAccessLayer(orthophotoTilesPath, {
      accessToken,
      tms: true,
      zIndex: 1,
      ...config.prescanOrthophotoLayerOptions,
    });
    this.state.map?.addLayer(layer);
  }

  private async fetchOrthophotoTiles() {
    const { customerId, siteId, dispatch } = this.props;

    try {
      const { orthophotoTilesPath } = await dispatch(
        fetchPrescan(customerId, siteId),
      );
      return orthophotoTilesPath;
    } catch ({ code, message }) {
      if (code === "PRESCAN_NOT_FOUND") {
        try {
          const { orthophotoTilesPath } = await dispatch(
            fetchMaidenScan(customerId, siteId),
          );
          return orthophotoTilesPath;
        } catch ({ code, message }) {
          if (code === "MAIDEN_SCAN_NOT_FOUND") {
            return;
          }
          alert(`Unexpected error loading maiden scan: ${message}`);
        }
        // TODO: this is to handle a case when site is created in csu but not on s3.
        // (it is only created after a file is uploaded in site creation workflow)

        // This should be removed once this case in included in BAPI 404 case
        // Task to track: https://app.asana.com/0/1205315643275024/1205560472326518
      } else if (code === "SITE_NOT_FOUND") {
        console.warn("No prescan available, site s3 folder is not created");
      } else {
        alert(`Unexpected error loading prescan: ${message}`);
      }
    }
  }

  componentDidUpdate(): void {
    this.mapRenderSourcePolygon();
  }

  handleOnConfirm = (): void => {
    const { value } = this.state;
    if (!value) {
      return;
    }
    if (MapPolygonEditControl.polygonHasSelfIntersections(value)) {
      alert("Polygon has self intersections! Please correct them.");
      return;
    }

    this.props.onChange(value);
    this.setState({
      value: null,
    });
  };

  handleOnCancel = (): void => {
    this.setState({
      value: null,
    });
  };

  mapRenderSourcePolygon(): void {
    const { value } = this.props;
    const { map } = this.state;

    if (!map || !value) {
      return;
    }

    this.sourceAreaPolygon &&
      map.hasLayer(this.sourceAreaPolygon) &&
      map.removeLayer(this.sourceAreaPolygon);

    // @ts-ignore
    this.sourceAreaPolygon = L.geoJson(value, { style: sourceAreaStyle });

    map.addLayer(this.sourceAreaPolygon);
    this.sourceAreaPolygon && this.sourceAreaPolygon.bringToBack();
  }

  render(): JSX.Element {
    const { map } = this.state;
    const { requestGcpSuggestion } = this.props;
    return (
      <MapContainerWithUiLayers
        mapElement={
          <div
            className={style({
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            })}
            ref={this.mapContainer}
          />
        }
        uiLayers={
          map
            ? [
                <MapControlsLayout
                  key="corner_controls"
                  topLeft={
                    <MapPolygonEditControl
                      onChange={this.handlePolygonChange}
                      value={this.state.value || this.props.value}
                      map={map}
                      clearAreaDisabled={true}
                      allowSelfIntersection={false}
                    />
                  }
                  bottomLeft={
                    <MapBaseLayersControl
                      map={map}
                      layers={[MAPBOX_SATELLITE, OSM_RASTER_LAYER]}
                    />
                  }
                  bottomRight={
                    <LegendControl legends={this.getLegendsList()} />
                  }
                  bottomCenter={
                    requestGcpSuggestion.show ? (
                      <FadeOutContainer
                        show={
                          requestGcpSuggestion.status === "DONE" &&
                          requestGcpSuggestion.show
                        }
                        delayMs={10000}
                        durationMs={600}
                        onAnimationEnd={this.props.onUpdateRequestGcpSuggestion.bind(
                          this,
                          false,
                        )}
                      >
                        <div
                          className={classes(
                            centeredAbsolutePopupStyle,
                            "request-gcp-suggestion-popup-container",
                          )}
                        >
                          <RequestGcpSuggestionPopup
                            status={requestGcpSuggestion.status}
                            onRequest={this.props.onUpdateRequestGcpSuggestion.bind(
                              this,
                              true,
                            )}
                            onHide={this.props.onUpdateRequestGcpSuggestion.bind(
                              this,
                              false,
                            )}
                          />
                        </div>
                      </FadeOutContainer>
                    ) : null
                  }
                  topRight={
                    <UploadButtonsControl
                      onGeoJsonUploaded={(geoJson) =>
                        this.setState({
                          value: {
                            type: "Feature",
                            properties: this.props.value.properties,
                            geometry: geoJson.geometry,
                          },
                        })
                      }
                    />
                  }
                />,
                <MapControlsLayout
                  key="center_controls"
                  middleLeft={<MapZoomControl map={map} />}
                  topCenter={
                    !this.state.value ? null : (
                      <MapControlConfirm
                        onCancel={this.handleOnCancel}
                        onConfirm={this.handleOnConfirm}
                      />
                    )
                  }
                />,
              ]
            : []
        }
      />
    );
  }

  getLegendsList(): Legend[] {
    return Object.keys(this.state.legends).map(
      (k: string) => this.state.legends[k],
    );
  }
}

const centeredAbsolutePopupStyle = style({
  position: "absolute",
  bottom: 10,
  left: 0,
  right: 0,
  marginLeft: "auto",
  marginRight: "auto",
  width: 510,
});

export default connect()(SiteAreaEditor);
